import React, { useEffect, useState } from "react";
import { Container } from "./styles";
import api from "../../../services/api";
import { addDays, format, subDays } from "date-fns";
import { DatePicker, Spin, Progress, Divider } from "antd";
import {
  MdAttachMoney,
  MdPeopleOutline,
  MdCheckCircleOutline,
  MdShowChart,
  MdInsights,
  MdTrendingUp,
  MdTimer,
  MdAutoGraph,
  MdHelpOutline,
} from "react-icons/md";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";
import { ViewInput } from "../../../GlobalStyles";
import { getValue } from "../../../services/functions";
import { toast } from "react-toastify";
import { useAuth } from "../../../hooks/AuthContext";

const { RangePicker } = DatePicker;

export default function HomeAssociado() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [stats, setStats] = useState([]);
  const [resume, setResume] = useState({});
  const [conversionRate, setConversionRate] = useState(0);
  const [totalLeads, setTotalLeads] = useState(0);
  const [isLoading, setIsLoading] = useState(true);

  const [startDate, setStartDate] = useState(addDays(new Date(), -30));
  const [endDate, setEndDate] = useState(new Date());

  useEffect(() => {
    loadDashboard();
  }, []);

  useEffect(() => {
    loadDashboard();
  }, [startDate, endDate]);

  async function loadDashboard() {
    setIsLoading(true);
    await api
      .get(`/resume/associate?startDate=${startDate}&endDate=${endDate}`)
      .then((response) => {
        const resume = response.data;
        setResume(resume);
        setStats([
          { name: "Pendente", value: resume.totalLeadsPendente, color: "#999" },
          { name: "Oportunidade", value: resume.totalLeadsOportunidade },
          { name: "Abordagem", value: resume.totalLeadsAbordagem },
          { name: "Atendimento", value: resume.totalLeadsAtendimento },
          { name: "Qualificação", value: resume.totalLeadsQualificacao },
          { name: "Proposta Enviada", value: resume.totalLeadsPropostaEnviada },
          { name: "Negociação", value: resume.totalLeadsNegociacao },
          {
            name: "Cliente Perdido",
            value: resume.totalLeadsClientePerdido,
            color: "#f44336",
          },
          {
            name: "Cliente Fechado",
            value: resume.totalLeadsClienteFechado,
            color: "#008037",
          },
        ]);
      })
      .catch(({ response }) => {
        if (response?.data?.message) {
          toast.warn(response.data.message);
        } else {
          toast.error("Erro Interno. verifique sua conexão e tente novamente");
        }
      });
    setIsLoading(false);
  }

  const handleStatusClick = (statusName) => {
    navigate("/associado/leads", { state: { filterStatus: statusName } });
  };

  const activeLeadsCount = stats
    .filter((s) => !["Cliente Fechado", "Cliente Perdido"].includes(s.name))
    .reduce((a, b) => a + b.value, 0);

  const statusExplanations = [
    {
      name: "Pendente",
      desc: "Aguardando algum franqueado receber o seu lead.",
    },
    {
      name: "Oportunidade",
      desc: "O lead foi aceito por um franqueado e está na fila para o primeiro contato.",
    },
    {
      name: "Abordagem",
      desc: "O lead já foi enviado e o contato inicial está sendo realizado.",
    },
    {
      name: "Atendimento",
      desc: "A equipe comercial está em conversação ativa para entender as necessidades.",
    },
    {
      name: "Qualificação",
      desc: "Análise técnica para confirmar se o projeto é viável para fechamento.",
    },
    {
      name: "Proposta Enviada",
      desc: "O orçamento formal foi encaminhado e aguarda resposta do cliente.",
    },
    {
      name: "Negociação",
      desc: "Fase final de ajustes de valores, prazos e termos da proposta.",
    },
    {
      name: "Cliente Fechado",
      desc: "Venda concluída com sucesso! Sua comissão está garantida.",
    },
    {
      name: "Cliente Perdido",
      desc: "O lead optou por não fechar o negócio no momento.",
    },
  ];

  return (
    <Container>
      <div className="dashboard-header">
        <h1>Resumo de Desempenho</h1>
        <ViewInput style={{ width: 280 }}>
          <p>Filtrar por Período</p>
          <RangePicker
            style={{ width: "100%" }}
            value={[dayjs(startDate), dayjs(endDate)]}
            format={"DD/MM/YYYY"}
            onChange={(dates) => {
              if (dates) {
                setStartDate(dates[0].toDate());
                setEndDate(dates[1].toDate());
              }
            }}
            allowClear={false}
          />
        </ViewInput>
      </div>

      {isLoading ? (
        <div
          style={{ display: "flex", justifyContent: "center", padding: 100 }}
        >
          <Spin size="large" color="#008037" />
        </div>
      ) : (
        <>
          <h2 className="section-title">Dados Gerais</h2>
          <div className="main-tags">
            <div className="tag-item highlight">
              <div className="tag-header">
                <MdAttachMoney size={20} /> Total Já Recebido
              </div>
              <span className="value">
                {getValue(resume?.totalPaymentsValue || 0)}
              </span>
            </div>
            <div className="tag-item">
              <div className="tag-header">
                <MdPeopleOutline size={20} /> Total de Leads Cadastrados
              </div>
              <span className="value">{resume?.totalLeadsCreated || 0}</span>
            </div>
            <div className="tag-item warning">
              <div className="tag-header">
                <MdCheckCircleOutline size={20} /> Sua Taxa de Comissão
              </div>
              <span className="value">{user.comission || 0}%</span>
            </div>
          </div>

          <h2 className="section-title">Desempenho no Período</h2>
          <div className="main-tags">
            <div className="tag-item highlight">
              <div className="tag-header">
                <MdAttachMoney size={20} /> Comissões Recebidas no Período
              </div>
              <span className="value">
                {getValue(resume?.totalLeadsClienteFechadoValue || 0)}
              </span>
            </div>
            <div className="tag-item">
              <div className="tag-header">
                <MdPeopleOutline size={20} /> Leads Cadastrados no Período
              </div>
              <span className="value">{resume?.leadsPeriodCreated || 0}</span>
            </div>
            <div className="tag-item warning">
              <div className="tag-header">
                <MdTrendingUp size={20} /> Valor em Negociação no Período
              </div>
              <span className="value">
                {getValue(resume?.totalLeadsPotentialValue || 0)}
              </span>
            </div>
          </div>

          <h2 className="section-title">Acompanhamento de Status</h2>
          <div className="status-grid">
            {stats.map((item, index) => (
              <div
                className="status-tag"
                key={index}
                style={{
                  borderLeft: `4px solid ${item.color || "#008037"}`,
                  "--color": item.color || "#008037",
                }}
                onClick={() => handleStatusClick(item.name)}
              >
                <span className="label">{item.name}</span>
                <span className="count">{item.value}</span>
              </div>
            ))}
          </div>

          <div className="main-stats">
            <div className="insight-card">
              <div className="icon-wrapper green">
                <MdTrendingUp size={24} />
              </div>
              <label>Média por Venda</label>
              <div className="value-row">
                <h3>
                  {getValue(
                    resume?.totalLeadsClienteFechadoValue /
                      resume?.totalLeadsClienteFechado || 0,
                  )}
                </h3>
              </div>
              <div className="footer-info">
                <MdInsights size={14} />
                Sua comissão média por cada fechamento
              </div>
            </div>

            <div className="insight-card">
              <div className="icon-wrapper blue">
                <MdTimer size={24} />
              </div>
              <label>Em Andamento</label>
              <div className="value-row">
                <h3>
                  {resume.totalLeadsOportunidade +
                    resume.totalLeadsAbordagem +
                    resume.totalLeadsAtendimento +
                    resume.totalLeadsQualificacao +
                    resume.totalLeadsPropostaEnviada +
                    resume.totalLeadsNegociacao}
                </h3>
                <span className="unit">Leads</span>
              </div>
              <div className="footer-info">
                <MdPeopleOutline size={14} />
                Leads sendo trabalhados no momento
              </div>
            </div>

            <div className="insight-card conversion-insight">
              <div className="icon-wrapper orange">
                <MdAutoGraph size={24} />
              </div>
              <label>Aproveitamento</label>
              <div className="value-row">
                <h3>
                  {(resume.totalLeadsClienteFechado /
                    (resume.totalLeadsClienteFechado +
                      resume.totalLeadsClientePerdido)) *
                    100 || 0}
                </h3>
                <span className="unit">%</span>
              </div>
              <div className="progress-container">
                <Progress
                  percent={
                    (resume.totalLeadsClienteFechado /
                      (resume.totalLeadsClienteFechado +
                        resume.totalLeadsClientePerdido)) *
                      100 || 0
                  }
                  showInfo={false}
                  strokeColor="#f2760a"
                  strokeWidth={8}
                />
              </div>
            </div>
          </div>

          {/* Legenda de Status */}
          <div
            style={{
              marginTop: 50,
              background: "#f9f9f9",
              padding: 30,
              borderRadius: 20,
              border: "1px solid #eee",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                marginBottom: 20,
              }}
            >
              <MdHelpOutline size={24} color="#008037" />
              <h3 style={{ margin: 0 }}>Entenda as Etapas do Processo</h3>
            </div>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
                gap: "20px 40px",
              }}
            >
              {statusExplanations.map((item, index) => (
                <div
                  key={index}
                  style={{ display: "flex", flexDirection: "column", gap: 4 }}
                >
                  <strong style={{ color: "#333", fontSize: 14 }}>
                    {item.name}
                  </strong>
                  <p
                    style={{
                      color: "#666",
                      fontSize: 13,
                      margin: 0,
                      lineHeight: "1.4",
                    }}
                  >
                    {item.desc}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </Container>
  );
}
